import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Box, Stack, Typography, Chip, } from '@mui/material';
import { replaceStylesheetGlobals } from '../portal/theme/portalTheme';
import { useConversationStore } from '../ConversationStoreProvider';
import { useTokens } from '../tokens/TokensProvider';
import Image from './Image';
import { createPromptActivationHandler } from './promptInteraction';
const ProductSpecificPrompts = ({ prompts, headerMessage, onPromptClick, isLoading, pageType = 'product', interactionDisabled = false, }) => {
    const store = useConversationStore();
    const tokens = useTokens();
    const { stylesheet } = store;
    const [resolvedHeaderImage, setResolvedHeaderImage] = React.useState(undefined);
    const styleConfig = pageType === 'category'
        ? (store.clientAIConfig?.categoryPageSuggestedPromptsStyle
            ?? store.clientAIConfig?.pageSuggestedPromptsStyle)
        : store.clientAIConfig?.pageSuggestedPromptsStyle;
    const layout = styleConfig?.layout === 'horizontal' ? 'horizontal' : 'vertical';
    const headerImageSource = pageType === 'category'
        ? (store.clientAIConfig?.categoryAnswersHeaderImage
            ?? store.clientAIConfig?.productAnswersHeaderImage)
        : store.clientAIConfig?.productAnswersHeaderImage;
    const headerImageAlignment = (() => {
        const value = pageType === 'category'
            ? (store.clientAIConfig?.categoryAnswersHeaderImageAlignment
                ?? store.clientAIConfig?.productAnswersHeaderImageAlignment)
            : store.clientAIConfig?.productAnswersHeaderImageAlignment;
        return value === 'center' || value === 'right' ? value : 'left';
    })();
    let imageJustifyContent = 'flex-start';
    if (headerImageAlignment === 'center') {
        imageJustifyContent = 'center';
    }
    else if (headerImageAlignment === 'right') {
        imageJustifyContent = 'flex-end';
    }
    React.useEffect(() => {
        let cancelled = false;
        const setImage = async () => {
            if (!headerImageSource) {
                setResolvedHeaderImage(undefined);
                return;
            }
            if (typeof headerImageSource === 'object' && typeof headerImageSource.url === 'string') {
                setResolvedHeaderImage(headerImageSource);
                return;
            }
            if (typeof headerImageSource !== 'string') {
                setResolvedHeaderImage(undefined);
                return;
            }
            const asset = store.getOrLoad?.('asset', headerImageSource);
            if (!asset) {
                setResolvedHeaderImage(undefined);
                return;
            }
            const applyAssetImage = (url) => {
                if (cancelled || !url)
                    return;
                setResolvedHeaderImage({
                    url,
                    status: asset.status || 'loaded',
                    alt: asset.alt || asset.name || '',
                    className: asset.className,
                    avatar: false,
                });
            };
            const precachedUrl = asset.getPrecachedUrl?.('original');
            if (precachedUrl) {
                applyAssetImage(precachedUrl);
                return;
            }
            if (typeof asset.getSignedUrl === 'function') {
                const signedUrl = await asset.getSignedUrl('original');
                applyAssetImage(signedUrl?.url || '');
                return;
            }
            setResolvedHeaderImage(undefined);
        };
        setImage();
        return () => {
            cancelled = true;
        };
    }, [headerImageSource, store]);
    const iconUrlFromConfig = styleConfig?.promptPrefixIconUrl;
    const iconAssetID = styleConfig?.promptPrefixIconAssetID;
    const PROMPT_PREFIX_ICON_DEFAULT_PX = 16;
    const rawIconSize = styleConfig?.promptPrefixIconSize;
    let iconSizePx;
    if (typeof rawIconSize === 'number' && rawIconSize > 0 && Number.isFinite(rawIconSize)) {
        iconSizePx = Math.round(rawIconSize);
    }
    else if (typeof rawIconSize === 'string' && rawIconSize.trim()) {
        const n = parseFloat(rawIconSize.trim());
        if (n > 0 && Number.isFinite(n))
            iconSizePx = Math.round(n);
    }
    const iconSizeResolvedPx = iconSizePx ?? PROMPT_PREFIX_ICON_DEFAULT_PX;
    const [resolvedIconUrl, setResolvedIconUrl] = useState(iconUrlFromConfig);
    useEffect(() => {
        if (iconUrlFromConfig && typeof iconUrlFromConfig === 'string' && iconUrlFromConfig.trim()) {
            setResolvedIconUrl(iconUrlFromConfig.trim());
            return;
        }
        if (!iconAssetID || typeof iconAssetID !== 'string' || !iconAssetID.trim()) {
            setResolvedIconUrl(undefined);
            return;
        }
        const maybe = store;
        if (!maybe.getOrLoad) {
            setResolvedIconUrl(undefined);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const asset = await maybe.getOrLoad('asset', iconAssetID.trim());
                if (cancelled || !asset?.getSignedUrl)
                    return;
                const entry = await asset.getSignedUrl('original');
                if (!cancelled && entry?.url)
                    setResolvedIconUrl(entry.url);
            }
            catch {
                if (!cancelled)
                    setResolvedIconUrl(undefined);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [iconUrlFromConfig, iconAssetID, store]);
    const chipIcon = resolvedIconUrl
        ? (_jsx(Box, { component: "img", src: resolvedIconUrl, alt: "", "aria-hidden": true, sx: {
                width: `${iconSizeResolvedPx}px`,
                height: `${iconSizeResolvedPx}px`,
                objectFit: 'contain',
                display: 'block',
            } }))
        : undefined;
    if (!prompts?.length || isLoading) {
        return null;
    }
    const defaultHeader = pageType === 'category'
        ? tokens.getValue('categoryPromptHeader')
        : tokens.getValue('productPromptHeader');
    const headerText = headerMessage || defaultHeader;
    return (_jsx(Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            width: '100%',
            mb: 2,
        }, children: _jsxs(Box, { sx: {
                backgroundColor: replaceStylesheetGlobals(stylesheet, 'color_background'),
                borderColor: '#9b9b9b',
                borderRadius: 8,
                borderWidth: 1,
                color: replaceStylesheetGlobals(stylesheet, 'color_font'),
                margin: 0,
                padding: 2,
                shadow: 1,
                width: '100%',
            }, children: [resolvedHeaderImage?.url && (_jsx(Box, { sx: {
                        display: 'flex',
                        justifyContent: imageJustifyContent,
                        mb: 1.5,
                    }, children: _jsx(Image, { url: resolvedHeaderImage.url, status: resolvedHeaderImage.status, alt: resolvedHeaderImage.alt, className: resolvedHeaderImage.className, avatar: resolvedHeaderImage.avatar, customStyle: {
                            display: 'block',
                            maxWidth: '100%',
                            maxHeight: 120,
                            objectFit: 'contain',
                        } }) })), _jsx(Typography, { variant: "subtitle1", sx: {
                        mb: 1,
                        fontWeight: 'bold',
                        lineHeight: 1.2,
                    }, children: headerText }), _jsx(Stack, { direction: layout === 'horizontal' ? 'row' : 'column', useFlexGap: true, sx: { flexWrap: layout === 'horizontal' ? 'wrap' : undefined }, spacing: layout === 'horizontal' ? 2 : 1, children: prompts.map((prompt) => (_jsx(Chip, { icon: chipIcon, disabled: interactionDisabled, onClick: createPromptActivationHandler(interactionDisabled, onPromptClick, prompt, pageType), variant: "outlined", sx: {
                            textTransform: 'none',
                            borderStyle: 'none',
                            width: 'fit-content',
                            height: 'auto',
                            '& .MuiChip-icon': {
                                marginLeft: 1,
                                marginRight: 0,
                            },
                            '& .MuiChip-label': {
                                whiteSpace: 'normal',
                                paddingTop: 1,
                                paddingBottom: 1,
                            },
                        }, label: prompt, className: "site-system-chip-primary" }, prompt))) })] }) }));
};
ProductSpecificPrompts.defaultProps = {
    headerMessage: undefined, // Will use token-based default in component
    pageType: 'product',
    interactionDisabled: false,
};
export default ProductSpecificPrompts;
//# sourceMappingURL=ProductSpecificPrompts.js.map